import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import {
    Observable,
    Subject,
    map,
    of,
    switchMap,
    take,
    takeUntil,
    tap,
    forkJoin,
} from 'rxjs';
import { LoginService } from '@intranet/modules/login';
import {
    JobAction,
    JobActionSearchQuery,
    JobActionUpdate,
    ActionService,
    ActionManageDialog,
    ActionBulkDialog,
    JobActionView,
} from '@intranet/modules/actions';
import { ActionStatus, JobActionTimePeriod, JobActionType } from '../action.types';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';
import { PageEvent } from '@angular/material/paginator';
import { statuses, today } from '../action.utils';
import { BoardLane } from './board/board.types';
import { BulkDialogData } from '../dialogs/action-bulk/action-bulk-dialog.component';
import { HotkeyService } from 'src/app/core/services/hotkey.service';
import { UserService } from '@intranet/modules/users/user.service';
import { JobService } from '@intranet/modules/jobs/job.service';
import { NO_JOB_TYPES } from '@intranet/components/job-type-picker';
import { PlannedBlockStatusPromptService } from './planner/planned-block-status-prompt.service';
import { PlannerComponent } from './planner/planner.component';
import {
    AddToPlannerDialogComponent,
    AddToPlannerDialogData,
    AddToPlannerDialogResult,
} from './planner/add-to-planner-dialog/add-to-planner-dialog.component';
import {
    PlannedBlocksListDialogComponent,
    PlannedBlocksListDialogData,
    PlannedBlocksListDialogResult,
} from './planner/planned-blocks-list-dialog/planned-blocks-list-dialog.component';
import { PlannedBlockService } from './planner/planned-block.service';

@Component({
    selector: 'action-listing',
    templateUrl: './action-listing.component.html',
    styleUrls: ['./action-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionListingComponent implements OnInit, OnDestroy, OnChanges {
    // The Planner view (when shown) — refreshed after "Edit action" there.
    @ViewChild(PlannerComponent) plannerRef?: PlannerComponent;
    @Input() hideClient = false;
    @Input() hideJob = false;
    @Input() hideSearch = false;
    @Input() hideCreate = true;
    @Input() hideTabs = false;
    @Input() hideHeader = false;
    @Input() disableSort = false;
    @Input() jobId: number;
    @Input() clientId: number;
    @Input() allUsers = false;
    @Output() changed = new EventEmitter();
    @Output() dialogChanged: EventEmitter<{ action: JobAction; type: string }> =
        new EventEmitter();
    @Input() isPreview: boolean = false;
    @Input() copyMode: boolean = false;
    @Output() copySelectionChanged = new EventEmitter<JobAction[]>();
    @Input() hideMarginBar = false;
    @Input() hideTogglButton = false;
    @Input() showActions: boolean = false;
    @Input() matchTimerByDescription = true;

    owner: string;
    actions: JobAction[] = [];
    tickedActions: JobAction[] = [];
    filterQuery: JobActionSearchQuery;
    isLoading: boolean = false;
    loggedInUserId: string;
    isAdmin = false;
    totalResults = 0;
    boardLanes: BoardLane[];
    isPlannedTab = false;
    // The open tab, passed to tab-nav as [override]: the filters change the
    // page's URL, and without it tab-nav then re-picks its tab from that URL
    // (which never names one), so it fell back to Live while the list still
    // showed the open tab's actions.
    currentTab = 'live';
    // Bound to the Planner child component ([(pulloutOpen)]/(unplannedCountChange))
    // so the "Unscheduled" toggle can live in this page's own toolbar,
    // to the left of the view icons, rather than inside Planner's own —
    // a template reference variable on <planner> wouldn't reach that far
    // since it's declared inside a *ngIf's embedded view.
    plannerPulloutOpen = true;
    // Folded into the page's own <loading> indicator (see showLoading)
    // rather than Planner showing a second, separate spinner of its own.
    plannerBlocksLoading = false;
    alwaysShowClient = false;
    timePeriod: JobActionTimePeriod = JobActionTimePeriod.Today;
    type?: JobActionType;
    jobTypeIds: number[] = [];
    view?: JobActionView;
    isFirstQuery = true;
    searchTerm?: string;
    private viewSubscription;
    // Which shape of results (List = paged, Board = all, priority-sorted —
    // see ActionService.getAll) the shared jobActions currently hold, or
    // null once they've been cleared. Lets a switch back from Planner reuse
    // them when they still fit the view being returned to.
    private _resultsView: JobActionView | null = null;
    // Set by _applyView when the switch kept the existing results, so
    // onViewChange can refresh them quietly instead of behind the overlay.
    private _keptResultsOnViewSwitch = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    isInvokedViaRouter: boolean;
    marginData: { [jobId: number]: any } = {};

    constructor(
        public dialog: MatDialog,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _actionService: ActionService,
        private _loginService: LoginService,
        private _changeRef: ChangeDetectorRef,
        private hotkeyService: HotkeyService,
        private userService: UserService,
        private _jobService: JobService,
        private _plannedBlockStatusPrompt: PlannedBlockStatusPromptService,
        private _plannedBlockService: PlannedBlockService,
    ) {}

    ngOnInit() {
        //assign a flag telling me whether this component was invoked via the router or via its selector
        this.isInvokedViaRouter = this.constructor === this._activatedRoute.component;

        this.loggedInUserId = this._loginService.userId;
        this.isAdmin = this._loginService.checkAuth(['admin']);

        // Register hotkey for "Add a new action"
        this.hotkeyService.registerHotkey('ctrl+shift+a', 'Add a new action', () => {
          if (this._router.url.includes('/jobs/') && !this.isJobValueModalOpen()) {
              if (!this.isDialogOpen()) {
                  this.onActionCreate();
              }
            }
        });

        this._activatedRoute.queryParamMap.subscribe((params) => {

            if (this.isInvokedViaRouter) {
                // This code should only be used if the component was invoked via the router
                // Not when it's used as a subcomponent
                this.owner = params.get('owner');
                var forceDefaults = this.owner === 'self';
                if (forceDefaults) {
                    this.owner = this.loggedInUserId;
                }
                if (params.get('clientId'))
                {
                    this.clientId = Number(params.get('clientId'));
                    this.alwaysShowClient = true;
                }
                else {
                    this.clientId = null;
                }
                if (params.get('timePeriod'))
                {
                    this.timePeriod = params.get('timePeriod') as JobActionTimePeriod;
                }
                else {
                    this.timePeriod = forceDefaults ? JobActionTimePeriod.Today : null;
                }

                if (params.get('searchTerm')) {
                    this.searchTerm = params.get('searchTerm');
                } else {
                    this.searchTerm = null;
                }

                let typeparam = params.get('type');

                this.type = typeparam === "standard" ? JobActionType.Standard
                    : typeparam === "invoice" ? JobActionType.Invoice
                    : typeparam === "approval" ? JobActionType.Approval
                    : null;

                // Comma-separated list in the URL, e.g. ?jobTypeIds=3,7
                // (-1 = every job type unticked, see NO_JOB_TYPES)
                this.jobTypeIds = (params.get('jobTypeIds') || '')
                    .split(',')
                    .map(id => Number(id))
                    .filter(id => id > 0 || id === NO_JOB_TYPES);

                if (!this.owner && this.isFirstQuery) {
                    this.owner = !this.allUsers ? this.loggedInUserId : null;
                }
                let areParametersUnchanged = !!(
                    this.filterQuery
                    && this.filterQuery.clientId == this.clientId
                    && this.filterQuery.owner == this.owner
                    && this.filterQuery.timePeriod == this.timePeriod
                    && this.filterQuery.type == this.type
                    && (this.filterQuery.jobTypeIds || []).join(',') == this.jobTypeIds.join(',')
                    && this.filterQuery.searchTerm == this.searchTerm);
                if (!areParametersUnchanged) {
                    this.isLoading = true;
                    this._loadJobActions({
                        ...this.filterQuery,
                        clientId: this.clientId,
                        jobId: this.jobId,
                        type: this.type,
                        jobTypeIds: this.jobTypeIds,
                        owner: this.owner,
                        timePeriod: this.timePeriod,
                        searchTerm: this.searchTerm,
                        page: 1,
                        resultsPerPage: this.view === JobActionView.Board ? -1 : null,
                        isPaginating: false
                    }).subscribe({
                        complete: () => {
                            this.isLoading = false;
                            this.isFirstQuery = false;
                            this._changeRef.markForCheck();
                        }
                    });
                }
            } else {
                // This code should only be used if the component is a subcomponent,
                // not if it's being invoked via the router
                if (!!params.get('triggerAddAction')) {
                    this.onActionCreate();
                }
                if (this.jobId || this.clientId) {
                  this.isLoading = true;
                  this._loadJobActions({
                      ...this.filterQuery,
                      jobId: this.jobId,
                      clientId: this.clientId,
                      page: 1,
                      isPaginating: false,
                  }).subscribe({
                      complete: () => {
                          this.isLoading = false;
                          this._changeRef.markForCheck();
                      },
                  });
              }
            }
        });

        this._actionService.filterQuery$
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap((filterQuery) => {
                    this.filterQuery = filterQuery;
                    this._changeRef.markForCheck();
                })
            )
            .subscribe();

        if (this.clientId || this.jobId) {
            // this.isLoading = true;
            // console.log("loading jobs from ngOnInit");
            // this._loadJobActions({
            //     ...this.filterQuery,
            //     clientId: this.clientId,
            //     jobId: this.jobId,
            //     page: 1,
            //     isPaginating: false,
            // }).subscribe({
            //     complete: () => {
            //         this.isLoading = false;
            //         this._changeRef.markForCheck();
            //     },
            // });
        }

        this._actionService.jobActions$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (jobActions) => {
                    this.actions = jobActions;

                    // Extract unique jobIds
                    const jobIds = Array.from(new Set(jobActions.map(a => a.jobId)));

                    this._actionService.getMarginDataForJobs(jobIds).subscribe({
                        next: (marginDataArray) => {
                            this.marginData = {};
                            for (const item of marginDataArray) {
                                this.marginData[item.jobId] = item;
                            }

                            this._changeRef.markForCheck();
                        }
                    });

                    if (this.view === JobActionView.Board) {
                        this.boardLanes = statuses
                            .filter((s) => s.value !== ActionStatus.Archived)
                            .map((status) => ({
                                id: status.value,
                                title: status.title,
                                actions: jobActions
                                    .filter(
                                        (a) => a.actionStatus === status.value
                                    )
                                    .map((action) => ({
                                        id: action.jobActionId,
                                        action,
                                    })),
                            }));
                    }
                    this._changeRef.markForCheck();
                },
            });

        this._actionService.jobActionsTotalResults$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (jobActions) => {
                    this.totalResults = jobActions;
                    this._changeRef.markForCheck();
                },
            });

        this._actionService.view$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (view) => this._applyView(view),
            });

        this.dialogChanged
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap(({ action, type }) => {
                    return this._loadJobActions(this.filterQuery).pipe(
                        map((actions) => ({ actions, action, type }))
                    );
                })
            )
            .subscribe({
                next: ({ type }) => {
                    this.isLoading = false;

                    if (type === 'create-multi') {
                        this.onActionCreate();
                    }
                },
            });

        // Update actions when billable status changes from timer header
        this._jobService.billableStatusChanged$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ jobId, isBillable }) => {
                this.actions.forEach(a => {
                    if (a.jobId === jobId) { a.isBillable = isBillable; }
                });
                // Force new array reference so action-table's ngOnChanges fires
                this.actions = [...this.actions];
                this._changeRef.detectChanges();
            });

        // If not in preview, subscribe to the global view changes
        if (!this.isPreview) {
            this.viewSubscription = this._actionService.view$
              .pipe(takeUntil(this._unsubscribeAll))
              .subscribe({
                  next: (view) => this._applyView(view),
              });
        } else {
            // In preview mode, always force the list view
            this.view = JobActionView.List;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('actions' in changes) {
            this.actions = changes.actions.currentValue;
            this._changeRef.markForCheck();
        }
    }

    ngOnDestroy() {
        if (this.viewSubscription) {
            this.viewSubscription.unsubscribe();
        }
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
        this._actionService.reset();
    }

    onActionCreate(): void {
        this.openManageDialog({
            jobId: this.jobId,
        });
    }

    // "Edit action" from the Planner's menus: the same edit-action popup
    // the List and Board open; the Planner redraws once it's closed.
    // The Planner redraws only if something was actually saved (the dialog
    // closes with the saved action, or reports a change — e.g. a note);
    // just closing it leaves the Planner as it was.
    onPlannerEditAction({ jobActionId, propertyKey }: { jobActionId: number; propertyKey?: string }) {
        let changed = false;
        const changes = this.dialogChanged.subscribe(() => (changed = true));
        this.openManageDialog({ jobActionId, jobId: this.jobId, propertyKey })
            .afterClosed()
            .subscribe((result) => {
                changes.unsubscribe();
                // The saved action (the popup closes with it): the Planner
                // refreshes just what it touches, without reloading
                // everyone's blocks.
                if (changed || result) this.plannerRef?.refreshAfterActionEdit(result || null);
            });
    }

    onActionSelected(
        { action, nextActionStatus, propertyKey }: JobActionUpdate = {
            action: null,
            nextActionStatus: null,
            propertyKey: null,
        }
    ) {
        if (propertyKey === 'actionStatus') {
            return this.openManageDialog({
                jobActionId: action.jobActionId,
                jobId: this.jobId,
                nextActionStatus,
                propertyKey,
                //title: action.title,
            });
        } else {
            return this.openManageDialog({
                jobActionId: action.jobActionId,
                jobId: this.jobId,
                nextActionStatus,
                propertyKey,
                title: action.title,
            });
        }
    }

    keepTickedCallback?: (ids: number[]) => void = null;

    onBulkAction(actionsAndCallback: {actions: JobAction[], keepTicked: (ids:number[]) => void }) {
        // the actions might be 'stale' so select from the current list
        // of actions
        this.keepTickedCallback = actionsAndCallback.keepTicked;
        let actions = actionsAndCallback.actions.map((a) =>
            this.actions.find((action) => action.jobActionId === a.jobActionId)
        );
        this.openBulkDialog(actions);
    }

    onActionUpdate(action: JobAction) {
        if (action.deadlineDate && !action.scheduleDate) {
            alert(
                'You must set a scheduled date before setting a deadline date'
            );
            return;
        }
        if (action.deadlineDate && action.scheduleDate) {
            if (moment(action.deadlineDate).isBefore(action.scheduleDate)) {
                alert(
                    'Deadline date cannot be earlier than the scheduled date'
                );
                return;
            }
        }
        if (action.title.trim() === '') {
            alert('Title cannot be empty');
            return;
        }

        this._actionService.update(action).subscribe({
            complete: () => this.changed.emit(),
            error: (e) => console.log(e),
        });
    }

    // "Add to planner" in a row's ⋮ menu (Actions page only — see
    // plannerFeatures on the table). Once added, the row's Planned flag
    // shows straight away when the block is today or later, same as the
    // server would report it on the next load.
    onAddToPlanner(action: JobAction) {
        const dialogRef = this.dialog.open<AddToPlannerDialogComponent, AddToPlannerDialogData, AddToPlannerDialogResult>(
            AddToPlannerDialogComponent,
            {
                panelClass: 'AddToPlannerDialog', // see vendor/_material.scss
                data: { action },
            }
        );
        dialogRef.afterClosed().subscribe((result) => {
            if (!result?.block) return;
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            if (new Date(result.block.start) < startOfToday || action.hasPlannedBlock) return;
            action.hasPlannedBlock = true;
            this.actions = [...this.actions];
            this._changeRef.markForCheck();
        });
    }

    // "Planned blocks" in a row's ⋮ menu, or its Planned flag clicked: the
    // action's blocks from today on, each opening the same edit popup as the
    // planner's. If any was moved, removed or logged, the row's Planned flag
    // is checked again (it may have been the action's last block).
    onViewPlannedBlocks(action: JobAction) {
        const dialogRef = this.dialog.open<PlannedBlocksListDialogComponent, PlannedBlocksListDialogData, PlannedBlocksListDialogResult>(
            PlannedBlocksListDialogComponent,
            {
                panelClass: 'AddToPlannerDialog', // see vendor/_material.scss
                data: { action },
            }
        );
        dialogRef.afterClosed().subscribe((result) => {
            if (!result?.changed) return;
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            this._plannedBlockService.getPlannedActions([action.jobActionId], startOfToday).subscribe({
                next: (planned) => {
                    const hasPlannedBlock = planned.some((p) => p.jobActionId === action.jobActionId);
                    if (hasPlannedBlock === !!action.hasPlannedBlock) return;
                    action.hasPlannedBlock = hasPlannedBlock;
                    this.actions = [...this.actions];
                    this._changeRef.markForCheck();
                },
                // The flag just stays as it was until the next load.
                error: () => {},
            });
        });
    }

    onActionDelete(action: JobAction) {
        const dialogRef = this.dialog.open(AlertDialog, {
            width: '320px',
            data: {
                title: 'Are you sure?',
                message: `You are about to delete action: ${action.title}. Are you sure?`,
                confirm: true,
                confirmTitle: 'Yes',
            },
        });

        // Register the cancel hotkey (Ctrl + Q) to close the dialog
        const cancelHotkeyHandler = () => {
            if (dialogRef) {
                dialogRef.close();
            }
        };
        this.hotkeyService.registerHotkey('ctrl+q', 'Close modal', cancelHotkeyHandler);

        dialogRef
            .afterClosed()
            .pipe(
                tap(() => {
                    // Unregister the hotkey when dialog is closed
                    this.hotkeyService.unregisterHotkey('ctrl+q');
                }),
                switchMap((result) => {
                    if (!result) {
                        return of(null);
                    }
                    // Then, if it has planned blocks after today, whether to
                    // remove those too (past and today's always stay).
                    return this._plannedBlockStatusPrompt.askBeforeDelete(action).pipe(
                        switchMap((removePlannedFrom) => this._actionService.delete(action.jobActionId, removePlannedFrom))
                    );
                }),
                tap((response) => {
                    if (response) {
                        this._plannedBlockStatusPrompt.onActionDeleted();
                        this.changed.emit();
                    }
                })
            )
            .subscribe();
    }

    // This method is called from the inner action-table’s output when copy mode is active.
    onCopySelectionChanged(selectedActions: JobAction[]): void {
        // Simply forward the selected actions to the parent.
        this.copySelectionChanged.emit(selectedActions);
    }

    onBoardCardSelected({ action, type }: { action: JobAction; type: string }) {
        // A card's Planned flag: same as the list's.
        if (type === 'plannedBlocks') {
            this.onViewPlannedBlocks(action);
            return;
        }

        if (type === 'job') {
            this._router.navigateByUrl(
                `/jobs/detail/${action.jobId}?highlight=${action.jobActionId}`
            );
            return;
        }

        this.onActionSelected({
            action,
            propertyKey: type === 'actionNotes' ? 'actionNotes' : null,
        });
    }

    onBoardCardDropped({
        status,
        action,
    }: {
        status: ActionStatus;
        action: JobAction;
    }) {
        if (status === ActionStatus.Waiting) {
            this.onActionSelected({
                action,
                nextActionStatus: status,
                propertyKey: 'actionStatus',
            })
                .afterClosed()
                .pipe(
                    take(1),
                    tap((response) => {
                        if (!response) {
                            this._actionService.reload();
                        }
                    })
                )
                .subscribe();
        } else {
            action.actionStatus = status;
            this.onActionUpdate(action);
        }
    }

    // Planner's blocks fetch only counts towards the overlay while Planner
    // is actually showing — once it's been switched away from, nothing is
    // left to ever report that fetch as finished.
    // Whether this listing shows the Planner (rather than the list/board).
    // Before view$ has first fired (the page's very first load) this.view
    // isn't set yet, so the shared view decides — except in preview, which
    // is always a list (see ngOnInit).
    private get isShowingPlanner(): boolean {
        const view = this.view ?? (this.isPreview ? JobActionView.List : this._actionService.view);
        return view === JobActionView.Planner;
    }

    get showLoading(): boolean {
        return this.isLoading || (this.view === JobActionView.Planner && this.plannerBlocksLoading);
    }

    onViewChange(view: JobActionView) {
        if (view === JobActionView.Planner) {
            // Planner does its own fetching (planned blocks, unscheduled
            // pull-out) and never shows the shared list/board results, so
            // there's nothing to load for it here.
            this.plannerBlocksLoading = false;
            return;
        }

        // Coming back from Planner to the view the results were already
        // loaded for: they're on screen straight away (see _applyView), so
        // just refresh them quietly in case anything changed meanwhile,
        // rather than blanking the page behind the overlay.
        const showLoading = !this._keptResultsOnViewSwitch || !this.actions.length;
        this._loadJobActions(this.filterQuery, showLoading).subscribe({
            complete: () => {
                this.isLoading = false;
                this._changeRef.markForCheck();
            },
            error: () => {
                this.isLoading = false;
                this._changeRef.markForCheck();
            },
        });

        // Reset only after the load call above: getAll() pushes the query
        // out through filterQuery$, which <planner> (not destroyed until
        // the next change detection) reacts to by refetching its blocks and
        // emitting loadingChange(true) — the reply to that is then ignored
        // once it's destroyed, so that `true` would otherwise never clear.
        this.plannerBlocksLoading = false;
    }

    openBulkDialog(actions: JobAction[]) {
        const first = actions[0];
        const commonUser = actions.every(a => a.waitingUserId === first.waitingUserId)
            ? first.waitingUserId
            : null;
        const commonGroup = actions.every(a => a.waitingGroupId === first.waitingGroupId)
            ? first.waitingGroupId
            : null;

        const initialWaiting = commonUser ?? commonGroup;
        const initialStatus  = initialWaiting != null
            ? ActionStatus.Waiting
            : null;

        const dialogRef = this.dialog.open(ActionBulkDialog, {
          width: '980px',
          data: {
              // send the existing values in so the dialog picks them up
              actionStatus:          initialStatus,
              waitingOnUserOrGroupId: initialWaiting,
              scheduleDate:           null,
              deadlineDate:           null,
              clearDeadline:          false,
              priority:               null,
              owners:                 null,
              actions:                actions
          } as BulkDialogData
        });

        dialogRef.afterClosed().subscribe({
            next: result => {
                if (result) {
                    this.bulkUpdateActions(actions, result);
                }
            }
        });

        return dialogRef;
    }

    bulkUpdateActions(actions: JobAction[], updates: BulkDialogData) {
        let dateError = false;
        const keepTicked: JobAction[] = [];
        const calls: Observable<JobAction>[] = [];

        // build our list of calls, and collect invalid actions
        actions.forEach(action => {
            let isValid = true;
            if (updates.deadlineDate || updates.scheduleDate) {
                const dead = updates.deadlineDate ?? action.deadlineDate;
                const sched = updates.scheduleDate  ?? action.scheduleDate;
                if (dead && sched && moment(dead).isBefore(sched)) {
                    dateError = true;
                    isValid = false;
                }
            }
            if (!isValid) {
                keepTicked.push(action);
            } else {
                const updated = { ...action } as JobAction;

                if (updates.deadlineDate != null) updated.deadlineDate = updates.deadlineDate;
                if (updates.scheduleDate != null) updated.scheduleDate = updates.scheduleDate;
                if (updates.clearDeadline) updated.deadlineDate = null;
                if (updates.actionStatus != null) updated.actionStatus = updates.actionStatus;
                if (updates.priority != null) updated.priority = updates.priority;
                if (updates.jobId != null) updated.jobId = updates.jobId;

                if (updates.waitingOnUserOrGroupId != null) {
                    if (typeof updates.waitingOnUserOrGroupId === 'string') {
                        updated.waitingUserId = updates.waitingOnUserOrGroupId;
                        updated.waitingGroupId = null;
                    } else {
                        updated.waitingGroupId = updates.waitingOnUserOrGroupId;
                        updated.waitingUserId = null;
                    }
                }

                if (updates.owners) {
                    updated.assignedUsers = updates.owners.userIds;
                    updated.assignedGroups = updates.owners.groupIds;
                }

                calls.push(this._actionService.update(updated));
            }
        });

        // run them in parallel, wait for all to finish
        if (calls.length) {
            forkJoin(calls)
              .pipe(take(1))
              .subscribe({
                next: () => {
                    // restore ticks
                    this.keepTickedCallback(keepTicked.map(a => a.jobActionId));
                    this.tickedActions = keepTicked;

                    // reload from server *after* updates have landed
                    this.refreshActions();

                    if (dateError) {
                        alert(
                            'Some actions were not updated because the deadline date was earlier than the scheduled date'
                        );
                    }
                  },
                  error: () => {
                      // even on error, restore ticks & reload
                      this.keepTickedCallback(keepTicked.map(a => a.jobActionId));
                      this.tickedActions = keepTicked;
                      this.refreshActions();
                  }
              });
        } else {
            // nothing valid to update
            this.keepTickedCallback(keepTicked.map(a => a.jobActionId));
            this.tickedActions = keepTicked;
            if (dateError) {
                alert(
                    'Some actions were not updated because the deadline date was earlier than the scheduled date'
                );
            }
        }
    }

    openManageDialog({
        jobActionId,
        jobId,
        title,
        nextActionStatus,
        propertyKey,
    }: {
        jobId: number;
        jobActionId?: number;
        title?: string;
        nextActionStatus?: ActionStatus | null;
        propertyKey?: string;
    }) {
        const dialogRef = this.dialog.open(ActionManageDialog, {
            width: propertyKey === 'moveJob' ? '720px' : '980px',
            data: {
                jobActionId,
                jobId,
                propertyKey,
                nextActionStatus,
                title,
                updated: this.dialogChanged,
            },
        });

        dialogRef.afterClosed().subscribe({
            complete: () => {
                this.isLoading = false;
                this.changed.emit();
            },
            error: (error) => {},
        });

        return dialogRef;
    }

    // Called by the main filters
    onFilter(filterQuery: JobActionSearchQuery) {
        if (this.isInvokedViaRouter) {
            this._router.navigate(['/actions/listing'],
            {
                skipLocationChange: false,
                queryParams:
                {
                    type: filterQuery.type == JobActionType.Approval ? 'approval'
                        : filterQuery.type == JobActionType.Invoice ? 'invoice'
                        : filterQuery.type == JobActionType.Standard ? 'standard'
                        : '',
                    jobTypeIds: (filterQuery.jobTypeIds || []).join(','),
                    clientId: filterQuery.clientId || '',
                    owner: filterQuery.owner || '',
                    timePeriod: filterQuery.timePeriod || '',
                    searchTerm: filterQuery.searchTerm || ''
                }
            });
        } else {
            this.isLoading = true;
            this._loadJobActions({
                ...filterQuery,
                page: 1,
                resultsPerPage: this.view === JobActionView.Board ? -1 : null,
                isPaginating: false,
            }).subscribe({
                complete: () => {
                    this.isLoading = false;
                },
            });
        }
    }

    refreshActions() {
        this.isLoading = true;
        this._loadJobActions(this.filterQuery).subscribe({
            complete: () => (this.isLoading = false),
        });
    }

    onSort(prop: string) {
        this.isLoading = true;

        const { sortBy: prevSortBy, sortDir: prevSortDir } = this.filterQuery;
        this._loadJobActions({
            ...this.filterQuery,
            sortBy: prop,
            sortDir:
                prop === prevSortBy
                    ? prevSortDir === 'asc'
                        ? 'desc'
                        : 'asc'
                    : 'asc',
        }).subscribe({
            complete: () => {
                this.isLoading = false;
            },
        });
    }

    onPaginate(e: PageEvent) {
        this._loadJobActions({
            ...this.filterQuery,
            page: e.pageIndex + 1,
            isPaginating: true,
        }).subscribe({
            complete: () => {
                this.isLoading = false;
                document.getElementById('page').scrollTo(0, 0);
            },
        });
    }

    // Gets status from tabs and sets the listing ID
    onStatus(status: string): void {
        let currentStatus = status.toLowerCase();
        this.currentTab = currentStatus || 'live';
        this.isPlannedTab = currentStatus === 'planned';

        let nextStatus: number | null = null;
        if (currentStatus === 'completed') nextStatus = ActionStatus.Completed;
        if (currentStatus === 'archived') nextStatus = ActionStatus.Archived;

        // Planned isn't a status: it's the Live tab's actions (same filters)
        // that have a planned block today or later — see PlannedOnly on the
        // server. Carried in the query like the status, so the search bar's
        // filters keep it while this tab is open.
        this._loadJobActions({
            ...this.filterQuery,
            status: nextStatus,
            plannedOnly: this.isPlannedTab,
            page: 1,
            isPaginating: false,
        }).subscribe({
            complete: () => (this.isLoading = false),
        });
    }

    // Shared handler for the view$ subscriptions in ngOnInit (there are two,
    // so it's a no-op when the view hasn't actually changed).
    private _applyView(view: JobActionView) {
        const previous = this.view;
        if (previous !== undefined && previous === view) {
            this._changeRef.markForCheck();
            return;
        }

        // Planner never reads the shared results, so leave them in place
        // going into it, and reuse them coming back out if they're the
        // right shape for the view being returned to. Every other switch
        // (e.g. List <-> Board, which load differently) starts clean.
        const keepResults = previous !== undefined && (
            view === JobActionView.Planner
            || (previous === JobActionView.Planner && view === this._resultsView)
        );
        if (!keepResults) {
            this._actionService.reset();
            this._resultsView = null;
        }
        this._keptResultsOnViewSwitch = keepResults;
        this.view = view;
        this._changeRef.markForCheck();
    }

    private _loadJobActions(
        query?: JobActionSearchQuery,
        showLoading = true
    ): Observable<JobAction[]> {
        // If we are on a job detail page, we don't want any filters
        if (this.jobId) {
            query.owner = null;
            query.type = null;
            query.timePeriod = null;
            query.searchTerm = null;
        }

        // Planner view: it loads its own data from the shared query, and
        // never shows the list/board results — so just share the new query
        // instead of fetching (and waiting behind the page's loading
        // overlay for) a list nobody sees. The results held are now out of
        // date, so going back to List/Board loads them fresh (_applyView).
        if (this.isShowingPlanner) {
            this._resultsView = null;
            this._actionService.publishFilterQuery(query);
            return of(this.actions);
        }

        if (showLoading) {
            this.isLoading = true;
        }
        // getAll() shapes the request by the current view (see there).
        this._resultsView = this._actionService.view === JobActionView.Board
            ? JobActionView.Board
            : JobActionView.List;

        return this._actionService.getAll(query);
    }

    private isDialogOpen(): boolean {
        return this.dialog.openDialogs.length > 0;
    }

    private isJobValueModalOpen(): boolean {
      // The parent component should store a variable like `isJobValueOpen`
      return document.querySelector('job-value') !== null;
  }
}
